import {
  CalendarOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  Modal,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {FaRupeeSign} from 'react-icons/fa';

const {RangePicker} = DatePicker;

const fmtINR = v => `₹ ${(v || 0).toLocaleString('en-IN')}`;

const GenerateSalaryModal = ({
  open,
  onClose,
  employee,
  defaultDateRange,
  token,
  generateMutation,
}) => {
  const [form] = Form.useForm();
  const [localRange, setLocalRange] = useState(
    defaultDateRange || [dayjs().startOf('month'), dayjs().endOf('month')],
  );

  useEffect(() => {
    if (open && defaultDateRange) {
      setLocalRange(defaultDateRange);
    }
  }, [open, defaultDateRange]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  if (!employee) {return null;}

  // ── Use totalAdvancePending (remaining balance) — NOT advanceDeducted (original amount)
  const pendingAdvance = employee.totalAdvancePending || 0;
  const hasAdvance     = pendingAdvance > 0;

  const fromDateStr = localRange?.[0]?.format('YYYY-MM-DD');
  const toDateStr   = localRange?.[1]?.format('YYYY-MM-DD');

  const handleGenerate = () => {
    form.validateFields().then(values => {
      if (!fromDateStr || !toDateStr) {
        message.warning('Please select a valid date range');
        return;
      }

      const payload = {
        employeeId: employee.employeeId,
        fromDate: fromDateStr,
        toDate: toDateStr,
        token,
      };

      if (hasAdvance) {
        payload.survivalAmount = values.survivalAmount ?? 0;
      }

      generateMutation.mutate(payload, {
        onSuccess: data => {
          message.success({
            content: (
              <span>
          Salary generated for <strong>{employee.name}</strong> — Net paid:{' '}
                <strong>{fmtINR(data?.salaryPaid)}</strong>
              </span>
            ),
            duration: 4,
          });
          handleClose();
        },
        onError: err => {
          message.error(
            err?.response?.data?.message ||
      'Failed to generate salary. Please try again.',
          );
        },
      });
    });   
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={480}
      destroyOnClose
      footer={null}
      title={null}
      styles={{body: {padding: 0}}}
    >
      {/* ── Header ── */}
      <div
        className={'rounded-t-lg px-6 pt-5 pb-5'}
        style={{background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)'}}>
        <div className={'flex items-center gap-3 mb-4'}>
          <div className={'w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0'}>
            <ThunderboltOutlined className={'text-yellow-300 text-lg'} />
          </div>
          <div>
            <p className={'text-white font-bold text-base mb-0'}>Generate Salary</p>
            <p className={'text-blue-100 text-xs mb-0'}>
              Select a date range and confirm to generate
            </p>
          </div>
        </div>

        {/* Employee info strip */}
        <div className={'flex items-center gap-3 bg-white/10 rounded-xl px-3 py-2.5'}>
          <div className={'w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0'}>
            <UserOutlined className={'text-white text-sm'} />
          </div>
          <div className={'flex-1 min-w-0'}>
            <p className={'text-white font-semibold text-sm mb-0 truncate'}>{employee.name}</p>
            <p className={'text-blue-100 text-xs mb-0'}>
              {fmtINR(employee.perDayWages)} / day
            </p>
          </div>

          {/* Only show advance tag if employee actually has pending advance */}
          {hasAdvance && (
            <Tag
              icon={<WarningOutlined />}
              color={'orange'}
              className={'shrink-0 text-xs'}>
              Advance: {fmtINR(pendingAdvance)}
            </Tag>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className={'px-6 pt-5 pb-6'}>
        <Form
          form={form}
          layout={'vertical'}
          initialValues={{survivalAmount: 0}}>

          {/* Date Range */}
          <Form.Item
            label={(
              <span className={'flex items-center gap-1.5 font-semibold text-gray-700 text-sm'}>
                <CalendarOutlined className={'text-blue-500'} />
                Salary Period
              </span>
            )}
            required>
            <RangePicker
              value={localRange}
              onChange={val => val && setLocalRange(val)}
              format={'DD MMM YYYY'}
              allowClear={false}
              style={{width: '100%'}}
              size={'large'}
              disabledDate={current => current && current > dayjs().endOf('day')}
              renderExtraFooter={() => (
                <div className={'flex gap-1 py-1 px-1 flex-wrap'}>
                  {[
                    {
                      label: 'This Month',
                      range: [dayjs().startOf('month'), dayjs().endOf('month')],
                    },
                    {
                      label: 'Last Month',
                      range: [
                        dayjs().subtract(1, 'month').startOf('month'),
                        dayjs().subtract(1, 'month').endOf('month'),
                      ],
                    },
                    {
                      label: 'Last 7 Days',
                      range: [dayjs().subtract(6, 'day'), dayjs()],
                    },
                    {
                      label: 'Last 14 Days',
                      range: [dayjs().subtract(13, 'day'), dayjs()],
                    },
                  ].map(({label, range}) => (
                    <Button
                      key={label}
                      size={'small'}
                      type={'link'}
                      className={'text-xs px-2'}
                      onClick={() => setLocalRange(range)}>
                      {label}
                    </Button>
                  ))}
                </div>
              )}
            />
          </Form.Item>

          {/* Survival Amount — ONLY shown when employee has pending advance balance */}
          {hasAdvance && (
            <>
              <Alert
                type={'warning'}
                showIcon
                className={'mb-4'}
                message={'Pending Advance Detected'}
                description={
                  `This employee has ${fmtINR(pendingAdvance)} in pending advance balance. ` +
                  'Enter the minimum amount they must receive after deduction. ' +
                  'Remaining balance carries forward to the next period.'
                }
              />
              <Form.Item
                name={'survivalAmount'}
                label={(
                  <span className={'flex items-center gap-1.5 font-semibold text-gray-700 text-sm'}>
                    <FaRupeeSign className={'text-orange-500'} />
                    Survival Amount
                  </span>
                )}
                extra={'Minimum guaranteed pay after advance deduction.'}
                rules={[
                  {required: true, message: 'Enter a survival amount (0 if none)'},
                  {type: 'number', min: 0, message: 'Must be 0 or greater'},
                ]}>
                <InputNumber
                  className={'w-full'}
                  size={'large'}
                  prefix={'₹'}
                  min={0}
                  step={0.01}
                  placeholder={'e.g. 1000'}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={v => v.replace(/,/g, '')}
                />
              </Form.Item>
            </>
          )}

          <Divider className={'my-4'} />

          {/* Actions */}
          <div className={'flex gap-3 justify-end'}>
            <Button size={'large'} onClick={handleClose}>
              Cancel
            </Button>
            <Tooltip
              title={!fromDateStr || !toDateStr ? 'Select a date range first' : ''}
              placement={'top'}>
              <Button
                size={'large'}
                type={'primary'}
                icon={<ThunderboltOutlined />}
                loading={generateMutation.isPending}
                disabled={!fromDateStr || !toDateStr}
                onClick={handleGenerate}
                style={{
                  background: hasAdvance
                    ? 'linear-gradient(135deg, #f97316, #ea580c)'   // orange when advance pending
                    : 'linear-gradient(135deg, #1d4ed8, #2563eb)',  // blue when no advance
                  border: 'none',
                  minWidth: 160,
                }}>
                {generateMutation.isPending
                  ? 'Generating...'
                  : 'Generate Salary'}
              </Button>
            </Tooltip>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

GenerateSalaryModal.propTypes = {
  open:             PropTypes.bool.isRequired,
  onClose:          PropTypes.func.isRequired,
  employee:         PropTypes.shape({
    employeeId:          PropTypes.string,
    name:                PropTypes.string,
    perDayWages:         PropTypes.number,
    totalAdvancePending: PropTypes.number,  // ← updated: was advanceDeducted
  }),
  defaultDateRange: PropTypes.array,
  token:            PropTypes.string,
  generateMutation: PropTypes.object.isRequired,
};

GenerateSalaryModal.defaultProps = {
  employee:         null,
  defaultDateRange: null,
  token:            '',
};

export {GenerateSalaryModal};
export default GenerateSalaryModal;
